import { useEffect, useRef } from 'react';

function clearMarkers(markers) {
  markers.forEach((marker) => {
    marker.map = null;
  });
}

/**
 * Attach AdvancedMarkerElement pins to a google.maps.Map.
 * Uses importLibrary('marker') so the marker class comes from the same API
 * instance as the map (avoids "not an instance of MapsApiMap").
 */
export default function useBusinessLocationAdvancedMarkers(map, positions, pinIcons, onMarkerClick) {
  const markersRef = useRef([]);
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;

  useEffect(() => {
    let cancelled = false;
    const created = [];

    async function attachMarkers() {
      if (!map || !Array.isArray(positions) || !positions.length) return;
      if (!window.google?.maps?.Map || !(map instanceof window.google.maps.Map)) return;

      const markerLib = await window.google.maps.importLibrary('marker');
      if (cancelled) return;

      const AdvancedMarkerElement = markerLib?.AdvancedMarkerElement;
      if (!AdvancedMarkerElement) return;

      clearMarkers(markersRef.current);
      markersRef.current = [];

      positions.forEach((position, index) => {
        if (cancelled) return;
        if (typeof position.lat !== 'number' || typeof position.lng !== 'number') return;

        const iconUrl = pinIcons[position.type];
        const content = document.createElement('img');
        content.src = iconUrl;
        content.width = 32;
        content.height = 32;
        content.alt = position.name || 'Location';
        content.className = 'business-location-map-pin';

        const marker = new AdvancedMarkerElement({
          map,
          position: { lat: position.lat, lng: position.lng },
          content,
          title: position.name,
        });

        marker.addListener('click', () => onMarkerClickRef.current(index));
        created.push(marker);
      });

      if (!cancelled) markersRef.current = created;
    }

    attachMarkers().catch(() => {
      // Maps API / marker library failures surface in the console; keep UI up.
    });

    return () => {
      cancelled = true;
      clearMarkers(created);
      clearMarkers(markersRef.current);
      markersRef.current = [];
    };
  }, [map, positions, pinIcons]);
}
